/**
 * CRM API Functions
 *
 * API layer for the sales pipeline feature
 */

import { api } from '@/lib/api';
import type {
  PipelineBoard,
  PipelineOpportunity,
  PipelineSummary,
  PipelineFilters,
  PipelineStage,
  MoveOpportunityInput,
  TransformToQuoteInput,
  TransformToQuoteResult,
  CreateOpportunityInput,
  CrmActivityDetail,
  CrmActivityOutcome,
  CrmActivitiesResponse,
  CreateCrmActivityInput,
  UpdateCrmActivityInput,
  LoadMoreResponse,
  V4PipelineBoardResponse,
  V4PipelineOpportunityResponse,
  V4CrmActivityResponse,
  V4LoadMoreResponse,
  Company,
  CompanyFilters,
  CreateCompanyInput,
  V4CompanyResponse,
  Contact,
  ContactFilters,
  CreateContactInput,
  V4ContactResponse,
  CompanySite,
  CreateCompanySiteInput,
  V4CompanySiteResponse,
} from '../types';

// =============================================================================
// Response Validation & Transformers
// =============================================================================


/**
 * Transform V4 opportunity response to internal snake_case format
 * Throws error if required fields are missing to prevent corrupt data
 */
function transformOpportunity(opp: V4PipelineOpportunityResponse): PipelineOpportunity {
  // Validate required fields - throw if missing to prevent corrupt data
  if (!opp || typeof opp !== 'object') {
    throw new Error('[CRM API] Invalid opportunity response: expected object, got ' + typeof opp);
  }

  const missingFields: string[] = [];
  if (!opp.id) missingFields.push('id');
  // accountId is nullable for tender/prospect opportunities (company-only)
  if (!opp.title) missingFields.push('title');
  if (!opp.stage) missingFields.push('stage');

  if (missingFields.length > 0) {
    throw new Error(
      `[CRM API] Opportunity response missing required fields: ${missingFields.join(', ')}`
    );
  }

  return {
    id: opp.id,
    account_id: opp.accountId ?? '',
    account_name: opp.accountName ?? opp.companyName ?? 'Prospect',
    account_number: opp.accountNumber,
    title: opp.title,
    type: opp.type ?? 'new_business',
    stage: opp.stage,
    description: opp.description,
    estimated_value: opp.estimatedValue,
    currency: opp.currency ?? 'CAD',
    probability: opp.probability,
    weighted_value: opp.weightedValue,
    target_close_date: opp.targetCloseDate ?? opp.targetDate,
    stage_entered_at: opp.stageEnteredAt,
    days_in_stage: opp.daysInStage ?? 0,
    owner_id: opp.ownerId,
    owner_name: opp.ownerName,
    last_activity_at: opp.lastActivityAt,
    next_activity_at: opp.nextActivityAt,
    activity_count: opp.activityCount ?? 0,
    hubspot_deal_id: opp.hubspotDealId,
    hubspot_deal_stage: opp.hubspotDealStage,
    hubspot_amount: opp.hubspotAmount,
    hubspot_close_date: opp.hubspotCloseDate,
    converted_to_quote_id: opp.convertedToQuoteId,
    auto_detected: opp.autoDetected ?? false,
    priority: opp.priority,
    created_by: opp.createdBy,
    created_at: opp.createdAt ?? new Date().toISOString(),
    updated_at: opp.updatedAt ?? new Date().toISOString(),
    position: opp.position,
  };
}

/**
 * Transform V4 activity response to internal snake_case format
 * Throws error if required fields are missing to prevent corrupt data
 */
function transformActivity(act: V4CrmActivityResponse): CrmActivityDetail {
  // Validate required fields - throw if missing to prevent corrupt data
  if (!act || typeof act !== 'object') {
    throw new Error('[CRM API] Invalid activity response: expected object, got ' + typeof act);
  }

  const missingFields: string[] = [];
  if (!act.id) missingFields.push('id');
  if (!act.title) missingFields.push('title');
  if (!act.type) missingFields.push('type');
  if (!act.status) missingFields.push('status');
  if (!act.opportunityId) missingFields.push('opportunityId');
  if (!act.accountId) missingFields.push('accountId');
  if (!act.createdByUserId) missingFields.push('createdByUserId');

  if (missingFields.length > 0) {
    throw new Error(
      `[CRM API] Activity response missing required fields: ${missingFields.join(', ')}`
    );
  }

  return {
    id: act.id,
    title: act.title,
    type: act.type,
    status: act.status,
    scheduled_at: act.scheduledAt,
    completed_at: act.completedAt,
    duration_minutes: act.durationMinutes,
    description: act.description,
    outcome: act.outcome,
    outcome_notes: act.outcomeNotes,
    opportunity_id: act.opportunityId,
    account_id: act.accountId,
    contact_ids: act.contactIds,
    created_by_user_id: act.createdByUserId,
    created_by_name: act.createdByName,
    created_at: act.createdAt ?? new Date().toISOString(),
    updated_at: act.updatedAt ?? new Date().toISOString(),
  };
}

/**
 * Transform V4 pipeline board response to internal format
 * Validates response structure and provides safe defaults
 */
function transformPipelineBoard(data: V4PipelineBoardResponse): PipelineBoard {
  // Validate top-level structure
  if (!data || typeof data !== 'object') {
    console.error('[CRM API] Invalid pipeline board response: expected object');
    return {
      columns: [],
      summary: {
        total_opportunities: 0,
        total_pipeline_value: 0,
        weighted_pipeline_value: 0,
        avg_deal_size: 0,
        avg_days_in_pipeline: 0,
        conversion_rate: 0,
        by_stage: {},
        by_type: {},
      },
    };
  }

  // Validate columns array
  if (!Array.isArray(data.columns)) {
    console.warn('[CRM API] Pipeline board response missing columns array');
    data.columns = [];
  }

  // Validate summary object
  if (!data.summary || typeof data.summary !== 'object') {
    console.warn('[CRM API] Pipeline board response missing summary object');
    data.summary = {
      totalOpportunities: 0,
      totalPipelineValue: 0,
      weightedPipelineValue: 0,
      avgDealSize: 0,
      avgDaysInPipeline: 0,
      conversionRate: 0,
      byStage: {},
      byType: {},
    };
  }

  return {
    columns: data.columns.map((col) => ({
      key: col.key ?? 'unknown',
      title: col.title ?? 'Unknown Stage',
      color: col.color ?? '#6366f1',
      bgColor: col.bgColor ?? '#eef2ff',
      opportunities: Array.isArray(col.opportunities)
        ? col.opportunities.map(transformOpportunity)
        : [],
      totalValue: col.totalValue ?? 0,
      count: col.count ?? 0,
      loadedCount: col.loadedCount ?? col.opportunities?.length ?? 0,
      hasMore: col.hasMore ?? false,
    })),
    summary: {
      total_opportunities: data.summary.totalOpportunities ?? 0,
      total_pipeline_value: data.summary.totalPipelineValue ?? 0,
      weighted_pipeline_value: data.summary.weightedPipelineValue ?? 0,
      avg_deal_size: data.summary.avgDealSize ?? 0,
      avg_days_in_pipeline: data.summary.avgDaysInPipeline ?? 0,
      conversion_rate: data.summary.conversionRate ?? 0,
      by_stage: data.summary.byStage ?? {},
      by_type: data.summary.byType ?? {},
    },
  };
}

/**
 * Transform V4 load more response to internal format
 * Validates response structure and provides safe defaults
 */
function transformLoadMoreResponse(data: V4LoadMoreResponse): LoadMoreResponse {
  // Validate response structure
  if (!data || typeof data !== 'object') {
    console.error('[CRM API] Invalid load more response: expected object');
    return {
      stage: 'prospect_acquired',
      opportunities: [],
      total_count: 0,
      offset: 0,
      page_length: 0,
      has_more: false,
    };
  }

  // Validate opportunities array
  if (!Array.isArray(data.opportunities)) {
    console.warn('[CRM API] Load more response missing opportunities array');
    data.opportunities = [];
  }

  return {
    stage: data.stage ?? 'prospect_acquired',
    opportunities: data.opportunities.map(transformOpportunity),
    total_count: data.totalCount ?? 0,
    offset: data.offset ?? 0,
    page_length: data.pageLength ?? 0,
    has_more: data.hasMore ?? false,
  };
}

// =============================================================================
// Pipeline API Functions
// =============================================================================

/**
 * Get the full pipeline board with all stages and opportunities
 */
export async function getPipelineBoard(filters?: PipelineFilters): Promise<PipelineBoard> {
  const params = new URLSearchParams();

  if (filters?.date_from) params.set('dateFrom', filters.date_from);
  if (filters?.date_to) params.set('dateTo', filters.date_to);
  if (filters?.owner_id) params.set('ownerId', filters.owner_id);
  if (filters?.account_id) params.set('accountId', filters.account_id);
  if (filters?.account_manager) params.set('accountManager', filters.account_manager);
  if (filters?.type) params.set('type', filters.type);
  if (filters?.pipeline) params.set('pipeline', filters.pipeline);
  if (filters?.min_value) params.set('minValue', filters.min_value.toString());
  if (filters?.max_value) params.set('maxValue', filters.max_value.toString());
  if (filters?.search) params.set('search', filters.search);

  const query = params.toString();
  const url = `/api/v4/crm/pipeline${query ? `?${query}` : ''}`;

  const data = await api.get<V4PipelineBoardResponse>(url);
  return transformPipelineBoard(data);
}

/**
 * Get pipeline summary metrics only
 */
export async function getPipelineSummary(filters?: PipelineFilters): Promise<PipelineSummary> {
  const params = new URLSearchParams();

  if (filters?.date_from) params.set('dateFrom', filters.date_from);
  if (filters?.date_to) params.set('dateTo', filters.date_to);
  if (filters?.owner_id) params.set('ownerId', filters.owner_id);
  if (filters?.account_id) params.set('accountId', filters.account_id);
  if (filters?.account_manager) params.set('accountManager', filters.account_manager);
  if (filters?.type) params.set('type', filters.type);

  const query = params.toString();
  const url = `/api/v4/crm/pipeline/summary${query ? `?${query}` : ''}`;

  interface V4SummaryResponse {
    totalOpportunities: number;
    totalPipelineValue: number;
    weightedPipelineValue: number;
    avgDealSize: number;
    avgDaysInPipeline: number;
    conversionRate: number;
    byStage: Record<PipelineStage, { count: number; value: number }>;
    byType: Record<string, { count: number; value: number }>;
  }

  const data = await api.get<V4SummaryResponse>(url);

  return {
    total_opportunities: data.totalOpportunities,
    total_pipeline_value: data.totalPipelineValue,
    weighted_pipeline_value: data.weightedPipelineValue,
    avg_deal_size: data.avgDealSize,
    avg_days_in_pipeline: data.avgDaysInPipeline,
    conversion_rate: data.conversionRate,
    by_stage: data.byStage,
    by_type: data.byType as Record<string, { count: number; value: number }>,
  };
}

/**
 * Load more opportunities for a specific pipeline stage
 */
export async function loadMoreForStage(
  stage: PipelineStage,
  offset: number,
  filters?: PipelineFilters
): Promise<LoadMoreResponse> {
  const params = new URLSearchParams();

  params.set('offset', offset.toString());

  if (filters?.date_from) params.set('dateFrom', filters.date_from);
  if (filters?.date_to) params.set('dateTo', filters.date_to);
  if (filters?.owner_id) params.set('ownerId', filters.owner_id);
  if (filters?.account_id) params.set('accountId', filters.account_id);
  if (filters?.account_manager) params.set('accountManager', filters.account_manager);
  if (filters?.type) params.set('type', filters.type);
  if (filters?.min_value) params.set('minValue', filters.min_value.toString());
  if (filters?.max_value) params.set('maxValue', filters.max_value.toString());
  if (filters?.search) params.set('search', filters.search);

  const query = params.toString();
  const url = `/api/v4/crm/pipeline/stage/${stage}/more${query ? `?${query}` : ''}`;

  const data = await api.get<V4LoadMoreResponse>(url);
  return transformLoadMoreResponse(data);
}

// =============================================================================
// Opportunity CRUD API Functions
// =============================================================================

/**
 * Create a new opportunity
 */
export async function createOpportunity(
  input: CreateOpportunityInput
): Promise<PipelineOpportunity> {
  const url = `/api/v4/accounts/${input.account_id}/opportunities`;

  const data = await api.post<V4PipelineOpportunityResponse>(url, {
    title: input.title,
    type: input.type,
    description: input.description || undefined,
    estimatedValue: input.estimated_value,
    targetDate: input.target_close_date || undefined,
    priority: input.priority,
  });

  return transformOpportunity(data);
}

// =============================================================================
// Opportunity Stage API Functions
// =============================================================================

/**
 * Move an opportunity to a new stage.
 * Uses the account-scoped endpoint when an accountId is present, and falls back
 * to the global /v4/opportunities/:id/stage endpoint for tender-sourced
 * opportunities where accountId is null.
 */
export async function moveOpportunityStage(
  accountId: string | null | undefined,
  opportunityId: string,
  input: MoveOpportunityInput
): Promise<PipelineOpportunity> {
  if (accountId) {
    const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/stage`;
    const data = await api.put<V4PipelineOpportunityResponse>(url, {
      targetStage: input.target_stage,
      notes: input.notes,
    });
    return transformOpportunity(data);
  }

  const data = await api.put<V4PipelineOpportunityResponse>(
    `/api/v4/opportunities/${opportunityId}/stage`,
    { stage: input.target_stage, reason: input.notes },
  );
  return transformOpportunity(data);
}

/**
 * Transform an opportunity to a quote (terminal action)
 */
export async function transformOpportunityToQuote(
  accountId: string,
  opportunityId: string,
  input: TransformToQuoteInput
): Promise<TransformToQuoteResult> {
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/convert-to-quote`;

  interface V4TransformResponse {
    quoteId: string;
    quoteNumber: string;
    mongoQuoteId?: string;
  }

  const data = await api.post<V4TransformResponse>(url, {
    projectName: input.project_name,
    notes: input.notes,
  });

  return {
    quote_id: data.quoteId,
    quote_number: data.quoteNumber,
    mongo_quote_id: data.mongoQuoteId,
  };
}

/**
 * Mark an opportunity as lost.
 * For tender-sourced opportunities (no accountId), uses the standalone CRM endpoint.
 */
export async function markOpportunityLost(
  accountId: string | null | undefined,
  opportunityId: string,
  reason?: string
): Promise<PipelineOpportunity> {
  const url = accountId
    ? `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/lost`
    : `/api/v4/opportunities/${opportunityId}/lost`;

  const data = await api.post<V4PipelineOpportunityResponse>(url, {
    reason,
  });

  return transformOpportunity(data);
}

// =============================================================================
// Activity API Functions
// =============================================================================

/**
 * Get activities for an opportunity
 */
export async function getOpportunityActivities(
  accountId: string,
  opportunityId: string,
  params?: {
    status?: string;
    type?: string;
    page_number?: number;
    page_length?: number;
  }
): Promise<CrmActivitiesResponse> {
  const searchParams = new URLSearchParams();

  if (params?.status) searchParams.set('status', params.status);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.page_number) searchParams.set('pageNumber', params.page_number.toString());
  if (params?.page_length) searchParams.set('pageLength', params.page_length.toString());

  const query = searchParams.toString();
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/activities${query ? `?${query}` : ''}`;

  interface V4ActivitiesResponse {
    activities: V4CrmActivityResponse[];
    summary: {
      total: number;
      scheduled: number;
      completed: number;
      byType: Record<string, number>;
      lastActivityAt?: string;
      nextScheduledAt?: string;
    };
    pageNumber: number;
    pageLength: number;
    total: number;
  }

  const data = await api.get<V4ActivitiesResponse>(url);

  return {
    activities: data.activities.map(transformActivity),
    summary: {
      total: data.summary.total,
      scheduled: data.summary.scheduled,
      completed: data.summary.completed,
      by_type: data.summary.byType as Record<string, number>,
      last_activity_at: data.summary.lastActivityAt,
      next_scheduled_at: data.summary.nextScheduledAt,
    },
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

/**
 * Create a new activity for an opportunity
 */
export async function createActivity(
  accountId: string,
  opportunityId: string,
  input: CreateCrmActivityInput
): Promise<CrmActivityDetail> {
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/activities`;

  const data = await api.post<V4CrmActivityResponse>(url, {
    title: input.title,
    type: input.type,
    scheduledAt: input.scheduled_at,
    durationMinutes: input.duration_minutes,
    description: input.description,
    contactIds: input.contact_ids,
  });

  return transformActivity(data);
}

/**
 * Update an activity
 */
export async function updateActivity(
  accountId: string,
  opportunityId: string,
  activityId: string,
  input: UpdateCrmActivityInput
): Promise<CrmActivityDetail> {
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/activities/${activityId}`;

  const data = await api.put<V4CrmActivityResponse>(url, {
    title: input.title,
    type: input.type,
    status: input.status,
    scheduledAt: input.scheduled_at,
    completedAt: input.completed_at,
    durationMinutes: input.duration_minutes,
    description: input.description,
    outcome: input.outcome,
    outcomeNotes: input.outcome_notes,
    contactIds: input.contact_ids,
  });

  return transformActivity(data);
}

/**
 * Mark an activity as completed
 */
export async function completeActivity(
  accountId: string,
  opportunityId: string,
  activityId: string,
  outcome?: CrmActivityOutcome,
  outcomeNotes?: string
): Promise<CrmActivityDetail> {
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/activities/${activityId}/complete`;

  const data = await api.patch<V4CrmActivityResponse>(url, {
    outcome,
    outcomeNotes,
  });

  return transformActivity(data);
}

/**
 * Delete an activity
 */
export async function deleteActivity(
  accountId: string,
  opportunityId: string,
  activityId: string
): Promise<void> {
  const url = `/api/v4/accounts/${accountId}/opportunities/${opportunityId}/activities/${activityId}`;
  await api.delete(url);
}

/**
 * Get upcoming activities across all opportunities (for dashboard widget)
 */
export async function getUpcomingActivities(params?: {
  account_id?: string;
  days?: number;
  limit?: number;
}): Promise<CrmActivityDetail[]> {
  const searchParams = new URLSearchParams();

  if (params?.account_id) searchParams.set('accountId', params.account_id);
  if (params?.days) searchParams.set('days', params.days.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const query = searchParams.toString();
  const url = `/api/v4/crm/activities/upcoming${query ? `?${query}` : ''}`;

  interface V4UpcomingResponse {
    activities: V4CrmActivityResponse[];
  }

  const data = await api.get<V4UpcomingResponse>(url);
  return data.activities.map(transformActivity);
}

// =============================================================================
// Account Manager API Functions
// =============================================================================

export interface AccountManager {
  id: string;
  name: string;
  isFormer?: boolean;
}

/**
 * Get curated list of sales managers for filtering
 * Returns same list as accounts/sales-managers for consistency
 */
export async function getAccountManagers(): Promise<AccountManager[]> {
  const url = '/api/v4/crm/account-managers';

  interface V4AccountManagersResponse {
    accountManagers: AccountManager[];
  }

  const data = await api.get<V4AccountManagersResponse>(url);
  return data.accountManagers;
}

// =============================================================================
// Company API Functions
// =============================================================================

function transformCompany(c: V4CompanyResponse): Company {
  return {
    id: c.id,
    account_id: c.accountId,
    name: c.name,
    type: c.type,
    status: c.status,
    industry: c.industry,
    website: c.website,
    phone: c.phone,
    email: c.email,
    address: c.address,
    city: c.city,
    province: c.province,
    country: c.country,
    postal_code: c.postalCode,
    domain: c.domain,
    logo_url: c.logoUrl ?? null,
    linkedin_url: c.linkedinUrl,
    employee_count: c.employeeCount,
    annual_revenue: c.annualRevenue != null ? Number(c.annualRevenue) : undefined,
    hubspot_company_id: c.hubspotCompanyId,
    apollo_company_id: c.apolloCompanyId,
    contact_count: c.contactCount ?? 0,
    notes: c.notes,
    tags: c.tags,
    created_by: c.createdBy,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

export async function getCompanies(
  filters?: CompanyFilters,
  page = 0,
  pageLength = 25,
  sortBy?: string,
  sortDirection?: 1 | -1,
): Promise<{ items: Company[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.type) params.set('type', filters.type);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.accountId) params.set('accountId', filters.accountId);
  params.set('pageNumber', page.toString());
  params.set('pageLength', pageLength.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDirection) params.set('sortDirection', sortDirection.toString());

  const query = params.toString();
  const url = `/api/v4/crm/companies${query ? `?${query}` : ''}`;

  const data = await api.get<{ items: V4CompanyResponse[]; total: number }>(url);
  return {
    items: data.items.map(transformCompany),
    total: data.total,
  };
}

export async function createCompany(input: CreateCompanyInput): Promise<Company> {
  const data = await api.post<V4CompanyResponse>('/api/v4/crm/companies', input);
  return transformCompany(data);
}

export async function updateCompany(id: string, input: Partial<CreateCompanyInput>): Promise<Company> {
  const data = await api.put<V4CompanyResponse>(`/api/v4/crm/companies/${id}`, input);
  return transformCompany(data);
}

export async function deleteCompany(id: string): Promise<void> {
  await api.delete(`/api/v4/crm/companies/${id}`);
}

// =============================================================================
// Contact API Functions
// =============================================================================

function transformContact(c: V4ContactResponse): Contact {
  return {
    id: c.id,
    company_id: c.companyId,
    company_name: c.companyName,
    company_account_id: c.companyAccountId ?? null,
    first_name: c.firstName,
    last_name: c.lastName,
    email: c.email,
    phone: c.phone,
    mobile_phone: c.mobilePhone,
    title: c.title,
    department: c.department,
    source: c.source,
    status: c.status,
    is_primary: c.isPrimary,
    hubspot_contact_id: c.hubspotContactId,
    apollo_contact_id: c.apolloContactId,
    linkedin_url: c.linkedinUrl,
    linkedin_headline: c.linkedinHeadline,
    linkedin_about: c.linkedinAbout,
    linkedin_avatar_url: c.linkedinAvatarUrl,
    notes: c.notes,
    tags: c.tags,
    created_by: c.createdBy,
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  };
}

export async function getContacts(
  filters?: ContactFilters,
  page = 0,
  pageLength = 25,
  sortBy?: string,
  sortDirection?: 1 | -1,
): Promise<{ items: Contact[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.companyId) params.set('companyId', filters.companyId);
  params.set('pageNumber', page.toString());
  params.set('pageLength', pageLength.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDirection) params.set('sortDirection', sortDirection.toString());

  const query = params.toString();
  const url = `/api/v4/crm/contacts${query ? `?${query}` : ''}`;

  const data = await api.get<{ items: V4ContactResponse[]; total: number }>(url);
  return {
    items: data.items.map(transformContact),
    total: data.total,
  };
}

export async function createContact(input: CreateContactInput): Promise<Contact> {
  const data = await api.post<V4ContactResponse>('/api/v4/crm/contacts', input);
  return transformContact(data);
}

export async function updateContact(id: string, input: Partial<CreateContactInput>): Promise<Contact> {
  const data = await api.put<V4ContactResponse>(`/api/v4/crm/contacts/${id}`, input);
  return transformContact(data);
}

export async function deleteContact(id: string): Promise<void> {
  await api.delete(`/api/v4/crm/contacts/${id}`);
}

// =============================================================================
// Contact Detail API Functions
// =============================================================================

export async function getContactById(id: string): Promise<Contact> {
  const data = await api.get<V4ContactResponse>(`/api/v4/crm/contacts/${id}`);
  return transformContact(data);
}

// =============================================================================
// Company Detail API Functions
// =============================================================================

export async function getCompanyById(id: string): Promise<Company> {
  const data = await api.get<V4CompanyResponse>(`/api/v4/crm/companies/${id}`);
  return transformCompany(data);
}

export async function getCompanyContacts(
  companyId: string,
  page = 0,
  pageLength = 100,
): Promise<{ items: Contact[]; total: number }> {
  const params = new URLSearchParams();
  params.set('companyId', companyId);
  params.set('pageNumber', page.toString());
  params.set('pageLength', pageLength.toString());
  const url = `/api/v4/crm/companies/${companyId}/contacts?${params.toString()}`;
  const data = await api.get<{ items: V4ContactResponse[]; total: number }>(url);
  return {
    items: data.items.map(transformContact),
    total: data.total,
  };
}

// =============================================================================
// Company Sites (public.company_sites — migration 0273)
//
// ponytail: backend route GET/POST /api/v4/crm/companies/:id/sites and the
// company_sites table (migration 0273) land on a separate branch. Until then
// the list query degrades to empty (retry:false in the hook) so the panel
// renders its empty state instead of erroring. The contract below matches the
// 0273 schema (FK company_id + the company address columns).
// =============================================================================

function transformCompanySite(s: V4CompanySiteResponse): CompanySite {
  return {
    id: s.id,
    company_id: s.companyId,
    name: s.name,
    address: s.address,
    city: s.city,
    province: s.province,
    country: s.country,
    postal_code: s.postalCode,
    created_at: s.createdAt,
  };
}

export async function getCompanySites(companyId: string): Promise<CompanySite[]> {
  const data = await api.get<{ items: V4CompanySiteResponse[] }>(
    `/api/v4/crm/companies/${companyId}/sites`,
  );
  return (data.items ?? []).map(transformCompanySite);
}

export async function addCompanySite(
  companyId: string,
  input: CreateCompanySiteInput,
): Promise<CompanySite> {
  const data = await api.post<V4CompanySiteResponse>(
    `/api/v4/crm/companies/${companyId}/sites`,
    input,
  );
  return transformCompanySite(data);
}

export async function linkCompanyAccount(companyId: string, accountId: string): Promise<Company> {
  const data = await api.put<V4CompanyResponse>(`/api/v4/crm/companies/${companyId}/link-account`, { accountId });
  return transformCompany(data);
}

export async function unlinkCompanyAccount(companyId: string): Promise<Company> {
  const data = await api.put<V4CompanyResponse>(`/api/v4/crm/companies/${companyId}/unlink-account`, {});
  return transformCompany(data);
}

// =============================================================================
// Contact ↔ Account role assignments (canonical contact_roles surface)
// =============================================================================

export type ContactRole = 'contractual_manager' | 'technical_manager' | 'it_manager';

export interface ContactAccountRole {
  id: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  role: ContactRole;
  ordinal: number;
  isActive: boolean;
  assignedAt: string;
}

export async function getContactAccountRoles(contactId: string): Promise<ContactAccountRole[]> {
  const data = await api.get<{ accountRoles: ContactAccountRole[] }>(
    `/api/v4/crm/contacts/${contactId}/account-roles`,
  );
  return data.accountRoles || [];
}

export async function setContactAccountRole(
  contactId: string,
  input: { accountId: string; role: ContactRole; ordinal?: number; notes?: string },
): Promise<void> {
  await api.post(`/api/v4/crm/contacts/${contactId}/account-roles`, input);
}

export async function removeContactAccountRole(
  contactId: string,
  accountId: string,
  role: ContactRole,
): Promise<void> {
  const params = new URLSearchParams({ accountId, role });
  await api.delete(`/api/v4/crm/contacts/${contactId}/account-roles?${params.toString()}`);
}
